/**
 * PWPreviousRules.js
 * Pure business logic for Pregnant Women Previous Vaccination scenario
 *
 * Phase 2: Extracted from vaccinationutils.js and VaccinationTabs.jsx
 * This contains ALL TD vaccine rules for PW Previous tab (checkbox + date picker logic)
 */

import dayjs from 'dayjs';

// Import shared utilities from Phase 1
import {
  calculateDaysFromLMC,
  calculateDaysFromDose,
  decryptDoseNumber,
  hasValidVaccinationDate,
  calculateAssumedTDDate,
  getTDVaccinationRecords,
  getTdBoosterVaccinationRecord,
  isTdPwVaccine,
  isTdBoosterVaccine
} from '../pregnant-women-current/PWCurrentRules';

/**
 * Format availability reason when the minimum date falls today or in the future
 */
const getAvailabilityReason = (minDate, eddDate) => {
  if (eddDate && minDate.isAfter(dayjs(eddDate))) {
    return 'Vaccination date exceeds EDD';
  }
  return null;
};

/**
 * Get TD eligibility status for a specific dose in Previous Vaccination scenario
 * CORE BUSINESS LOGIC for PW Previous vaccinations (checkbox + date picker)
 * @param {Object} vaccine - Vaccine object
 * @param {number} doseIndex - Index of the dose (0 for TD-1, 1 for TD-2)
 * @param {string} lmcDate - Last Menstrual Cycle date
 * @param {Object} tdRecords - Object containing td1 and td2 records
 * @param {string} selectedTD1Date - Selected date for TD-1 in the form (for TD-2 calculation)
 * @returns {Object} - DoseConfig object with checkboxEnabled, minDate, maxDate properties
 */
export const getTDPreviousDoseStatus = (vaccine, doseIndex, lmcDate, tdRecords, selectedTD1Date = null, eddDate = null) => {
  const daysFromLMC = calculateDaysFromLMC(lmcDate);
  const { td1, td2 } = tdRecords;
  const df0 = vaccine.doseFrequencies?.[0] !== undefined ? vaccine.doseFrequencies[0] : 28;
  const df1 = vaccine.doseFrequencies?.[1] !== undefined ? vaccine.doseFrequencies[1] : 28;

  // TD-1 (First dose)
  if (doseIndex === 0) {
    // If already administered, show the date (calculated or actual) - no checkbox
    if (td1 && td1.vaccination_date) {
      return {
        status: 'administered',
        date: dayjs(td1.vaccination_date).format('DD/MM/YYYY'),
        checkboxEnabled: false,
        showCheckbox: false,
        showDatePicker: false,
        minDate: null,
        maxDate: null,
        isCalculated: td1.isCalculatedDate || false
      };
    }

    // If less than specific dose gap from LMC, disable checkbox
    if (daysFromLMC < df0) {
      return {
        status: 'disabled',
        checkboxEnabled: false,
        showCheckbox: true,
        showDatePicker: false,
        minDate: null,
        maxDate: null,
        reason: null,
        isCalculated: false
      };
    }

    // Gap+ days from LMC, enable checkbox
    // Date picker should allow dates from LMC+gap to yesterday (or eddDate)
    const minDate = dayjs(lmcDate).add(df0, 'day');
    const baseMaxDate = dayjs().subtract(1, 'day');
    const maxDate = eddDate && dayjs(eddDate).isBefore(baseMaxDate) ? dayjs(eddDate) : baseMaxDate;

    // Check if there are ANY valid dates available
    if (minDate.isAfter(maxDate, 'day')) {
      return {
        status: 'no_dates_available',
        checkboxEnabled: false,
        showCheckbox: true,
        showDatePicker: false,
        minDate: null,
        maxDate: null,
        reason: getAvailabilityReason(minDate, eddDate),
        isCalculated: false
      };
    }

    return {
      status: 'enabled',
      checkboxEnabled: true,
      showCheckbox: true,
      showDatePicker: true, // Will show when checkbox is selected
      minDate: minDate.toDate(),
      maxDate: maxDate.toDate(),
      isCalculated: false
    };
  }

  // TD-2 (Second dose)
  if (doseIndex === 1) {
    // If already administered, show the date (calculated or actual) - no checkbox
    if (td2 && td2.vaccination_date) {
      return {
        status: 'administered',
        date: dayjs(td2.vaccination_date).format('DD/MM/YYYY'),
        checkboxEnabled: false,
        showCheckbox: false,
        showDatePicker: false,
        minDate: null,
        maxDate: null,
        isCalculated: td2.isCalculatedDate || false
      };
    }

    // If TD-1 not given and not selected in form, disable checkbox
    if (!td1 && !selectedTD1Date) {
      return {
        status: 'disabled',
        checkboxEnabled: false,
        showCheckbox: true,
        showDatePicker: false,
        minDate: null,
        maxDate: null,
        reason: null,
        isCalculated: false
      };
    }

    // TD-1 is given or selected, check if we can enable TD-2
    const td1Date = td1 ? td1.vaccination_date : selectedTD1Date;

    if (!td1Date) {
      return {
        status: 'disabled',
        checkboxEnabled: false,
        showCheckbox: true,
        showDatePicker: false,
        minDate: null,
        maxDate: null,
        reason: 'TD-1 date required',
        isCalculated: false
      };
    }

    // Calculate min and max dates for TD-2
    const minDate = dayjs(td1Date).add(df1, 'day');
    const baseMaxDate = dayjs().subtract(1, 'day');
    const maxDate = eddDate && dayjs(eddDate).isBefore(baseMaxDate) ? dayjs(eddDate) : baseMaxDate;

    // Check if there are ANY valid dates available
    // If minDate is in the future (after maxDate), no dates are available
    if (minDate.isAfter(maxDate, 'day')) {
      return {
        status: 'no_dates_available',
        checkboxEnabled: false, // Disable checkbox - no valid dates
        showCheckbox: true,
        showDatePicker: false,
        minDate: null,
        maxDate: null,
        reason: getAvailabilityReason(minDate, eddDate),
        isCalculated: false
      };
    }

    // Valid dates exist, enable checkbox
    return {
      status: 'enabled',
      checkboxEnabled: true,
      showCheckbox: true,
      showDatePicker: true, // Will show when checkbox is selected
      minDate: minDate.toDate(),
      maxDate: maxDate.toDate(),
      isCalculated: false
    };
  }

  return {
    status: 'unknown',
    checkboxEnabled: false,
    showCheckbox: true,
    showDatePicker: false,
    minDate: null,
    maxDate: null,
    isCalculated: false
  };
};

/**
 * Check if a date should be disabled in the date picker
 * @param {Date} date - Date to check
 * @param {Date} minDate - Minimum allowed date
 * @param {Date} maxDate - Maximum allowed date
 * @returns {boolean} - True if date should be disabled
 */
export const shouldDisableDate = (date, minDate, maxDate) => {
  if (!minDate || !maxDate) return true;

  const checkDate = dayjs(date);
  const min = dayjs(minDate);
  const max = dayjs(maxDate);

  return checkDate.isBefore(min, 'day') || checkDate.isAfter(max, 'day');
};

/**
 * Get selected TD-1 date from form data (for TD-2 calculation)
 * @param {Object} vaccine - Vaccine object with availableDoses
 * @param {number} currentDoseIndex - Current dose index being processed
 * @returns {string|null} - Selected TD-1 date or null
 */
export const getSelectedTD1DateFromForm = (vaccine, currentDoseIndex, lmcDate = null, doseFrequency = 28) => {
  // Only relevant for TD-2 (index 1)
  if (currentDoseIndex !== 1) return null;

  const td1Dose = vaccine.availableDoses?.[0];
  if (td1Dose?.selected) {
    if (td1Dose.administeredDate) {
      // TD-1 has an explicit date – use it directly
      return td1Dose.administeredDate;
    }
    // TD-1 is checked but no date entered yet.
    // Return an assumed date (LMC + TD-1 frequency) so TD-2 can be enabled
    // and its minDate constrained to assumed_TD1 + TD-2 frequency.
    if (lmcDate) {
      return dayjs(lmcDate).add(doseFrequency, 'day').format('YYYY-MM-DD');
    }
  }

  return null;
};

/**
 * MAIN ENTRY POINT: Get dose status for PW Previous vaccination scenario
 * This is the unified interface that will be called by the UI components
 * @param {Object} vaccine - Vaccine object
 * @param {number} doseIndex - Index of the dose (0-based)
 * @param {Object} profileData - Profile data containing lmc_date and vaccination history
 * @param {Function} decryptFunction - Function to decrypt dose numbers
 * @param {Object} selectedDoses - Currently selected doses in the form (for TD-2 dependency)
 * @returns {Object} - DoseConfig object for UI consumption
 */
export const getPWPreviousDoseStatus = (
  vaccine,
  doseIndex,
  profileData,
  decryptFunction,
  selectedDoses = null,
  identityContext = null
) => {
  const identityCtx = { ...identityContext, decryptFunction };
  const { lmc_date, vaccination: administeredVaccinations, edd_date } = profileData || {};

  // TD Booster: single dose, same date window as TD-1 (LMC + gap to yesterday/EDD)
  if (isTdBoosterVaccine(vaccine, identityCtx)) {
    if (!lmc_date) {
      console.warn('No LMC date found for TD Booster eligibility');
      return {
        status: 'unknown',
        checkboxEnabled: false,
        showCheckbox: true,
        showDatePicker: false,
        minDate: null,
        maxDate: null,
        reason: 'LMC date not available',
        isCalculated: false
      };
    }

    const boosterRecord = getTdBoosterVaccinationRecord(
      administeredVaccinations || [],
      decryptFunction,
      lmc_date,
      vaccine.doseFrequencies,
      identityContext
    );
    const tdRecords = { td1: boosterRecord, td2: null };

    return getTDPreviousDoseStatus(vaccine, doseIndex, lmc_date, tdRecords, null, edd_date);
  }

  // PW Td (TD-1 / TD-2 sequence)
  if (isTdPwVaccine(vaccine, identityCtx)) {
    if (!lmc_date) {
      console.warn('No LMC date found for TD vaccine eligibility');
      return {
        status: 'unknown',
        checkboxEnabled: false,
        showCheckbox: true,
        showDatePicker: false,
        minDate: null,
        maxDate: null,
        reason: 'LMC date not available',
        isCalculated: false
      };
    }

    const tdRecords = getTDVaccinationRecords(
      administeredVaccinations || [],
      vaccine.name,
      decryptFunction,
      lmc_date,
      vaccine.doseFrequencies,
      identityContext
    );

    const df0ForAssumed = vaccine.doseFrequencies?.[0] !== undefined ? vaccine.doseFrequencies[0] : 28;
    const selectedTD1Date = getSelectedTD1DateFromForm(vaccine, doseIndex, lmc_date, df0ForAssumed);

    return getTDPreviousDoseStatus(vaccine, doseIndex, lmc_date, tdRecords, selectedTD1Date, edd_date);
  }

  // Generic logic for other PW vaccines
  const eddDate = profileData?.edd_date;
  const yesterday = dayjs().subtract(1, 'day');
  const maxDate = eddDate && dayjs(eddDate).isBefore(yesterday) ? dayjs(eddDate).toDate() : yesterday.toDate();
  return {
    status: 'enabled',
    checkboxEnabled: true,
    showCheckbox: true,
    showDatePicker: true,
    minDate: null,
    maxDate: maxDate,
    isCalculated: false
  };
};

/**
 * Validate TD-2 date when TD-1 date changes (cascade validation)
 * This function checks if existing TD-2 date is still valid when TD-1 date changes
 * @param {string} newTD1Date - New TD-1 date
 * @param {string} currentTD2Date - Current TD-2 date
 * @returns {Object} - Validation result with isValid and reason
 */
export const validateTD2DateAfterTD1Change = (newTD1Date, currentTD2Date, doseGap = 28) => {
  if (!newTD1Date || !currentTD2Date) {
    return { isValid: true, reason: null };
  }

  try {
    const td1Date = dayjs(newTD1Date);
    const td2Date = dayjs(currentTD2Date);
    const minTD2Date = td1Date.add(doseGap, 'day');

    // Check if TD-2 date is still valid (>= TD1 + gap days)
    if (td2Date.isBefore(minTD2Date, 'day')) {
      return {
        isValid: false,
        reason: `TD-2 date must be at least ${doseGap} days after TD-1 date (${minTD2Date.format('DD/MM/YYYY')})`
      };
    }

    return { isValid: true, reason: null };
  } catch (error) {
    console.error('Error validating TD-2 date:', error);
    return { isValid: false, reason: 'Date validation error' };
  }
};

/**
 * Get date picker constraints for a specific dose
 * @param {Object} doseConfig - Dose configuration from getPWPreviousDoseStatus
 * @returns {Object} - Date picker constraints
 */
export const getDatePickerConstraints = (doseConfig) => {
  if (!doseConfig.showDatePicker || !doseConfig.minDate || !doseConfig.maxDate) {
    return {
      shouldDisableDate: () => true, // Disable all dates
      minDate: null,
      maxDate: null
    };
  }

  return {
    shouldDisableDate: (date) => shouldDisableDate(date, doseConfig.minDate, doseConfig.maxDate),
    minDate: doseConfig.minDate,
    maxDate: doseConfig.maxDate
  };
};